// =====================================================================
// BONSAI FINANCIAL — CENTRAL CONFIGURATION
// ---------------------------------------------------------------------
// This is the SINGLE source of truth for all business information.
// To change the phone/WhatsApp number, email, services, analytics IDs
// etc. across the ENTIRE website, edit the values here ONCE.
// =====================================================================

export const site = {
  businessName: "Bonsai Financial",
  founderName: "Evan Dreyer",
  companyRegistration: "2021/118633/07", // Bonsai Financial company registration

  // ---- LOGO (official uploaded brand asset) ----
  logo: {
    header: "/bonsai-logo.png",   // full lockup (icon + BONSAI FINANCIAL)
    icon: "/bonsai-icon.png",     // square icon (favicon, compact use)
  },

  // ---- CONTACT (change once, updates everywhere) ----
  // Temporary personal number — replace with the dedicated Bonsai
  // business/WhatsApp number when available. International format.
  phoneDisplay: "+27 73 726 2280",
  phoneE164: "+27737262280",       // tel: links
  whatsappNumber: "27737262280",   // wa.me/<number> — no "+" or spaces

  // Configurable email — replace with the official address.
  email: "bonsaifinancial@outlook.com",

  // Configurable placeholders (not invented facts — leave until confirmed)
  address: "", // leave empty — do not imply a physical office until confirmed
  businessHours: "Monday – Friday, 08:00 – 17:00 (SAST)",

  // ---- PROFESSIONAL CREDENTIALS (verified positioning only) ----
  credentials: {
    designation: "Professional Accountant (SA)",
    professionalBody: "SAIPA",
    saipaRegistered: true,
    taxTechnician: "Tax Technician Practitioner (SA)",
    sait: true,
    trustLine: "SAIPA Registered | Professional Accountant (SA)",
    // Verified membership numbers — held in config but NOT publicly displayed
    // until Evan confirms approval to publish. Flip the flag to true once approved.
    showMembershipNumbers: false,
    saipaMembershipNumber: "46904",
    saitMembershipNumber: "2782264",
    practiceNumber: "",
  },

  // ---- SOCIAL (placeholders) ----
  social: {
    facebook: "",
    instagram: "",
    linkedin: "",
  },

  // ---- ANALYTICS (placeholders — do NOT invent IDs) ----
  analytics: {
    metaPixelId: "",          // e.g. "123456789012345"
    ga4MeasurementId: "",     // e.g. "G-XXXXXXXXXX"
    gtmId: "",                // e.g. "GTM-XXXXXXX"
  },
};

// ---- WHATSAPP PREFILLED MESSAGES (context-sensitive) ----
export const whatsappMessages = {
  general:
    "Hi Bonsai Financial. I visited your website and would like assistance with accounting or tax services.",
  bookkeeping:
    "Hi Bonsai Financial. I would like to enquire about your bookkeeping services.",
  tax: "Hi Bonsai Financial. I would like assistance with tax-related services.",
  consultation:
    "Hi Bonsai Financial. I would like to arrange a consultation.",
};

// ---- SERVICES ----
export const services = [
  {
    id: "bookkeeping",
    title: "Bookkeeping",
    short: "Keep your records organised, accurate and up to date.",
    items: [
      "Transaction processing",
      "Bank reconciliations",
      "Monthly bookkeeping",
      "Financial record maintenance",
      "Debtors / creditors support where applicable",
      "Monthly financial administration",
    ],
  },
  {
    id: "accounting",
    title: "Accounting & Financial Reporting",
    short: "Clear reporting so you understand how your business is performing.",
    items: [
      "Management accounts",
      "Financial reporting",
      "Annual financial statements",
      "Financial record preparation",
      "Accounting support",
      "Business performance reporting",
    ],
  },
  {
    id: "tax",
    title: "Tax Services",
    short: "Practical tax support led by a Tax Technician Practitioner.",
    items: [
      "Individual tax",
      "Business tax",
      "Tax return preparation and submission",
      "Provisional tax support",
      "VAT-related support",
      "SARS-related administration / support",
      "General tax administration",
    ],
  },
  {
    id: "payroll",
    title: "Payroll",
    short: "Reliable payroll processing and administration.",
    items: [
      "Payroll processing",
      "Payroll administration",
      "Employee payroll records",
      "PAYE / UIF-related administrative support where applicable",
    ],
  },
  {
    id: "business-support",
    title: "Business & Financial Support",
    short: "Ongoing financial organisation and insight for your business.",
    items: [
      "Financial administration",
      "Management reporting",
      "Business financial organisation",
      "Financial insights",
      "Ongoing financial support",
      "Business support",
    ],
  },
];

// ---- INDUSTRIES / WHO WE HELP ----
export const industries = [
  { slug: "medical-professionals", name: "Medical Professionals",
    challenge: "Busy practitioners who need their practice finances handled accurately while they focus on patients." },
  { slug: "legal-professionals", name: "Legal Professionals",
    challenge: "Attorneys and firms needing dependable bookkeeping, tax and reporting support." },
  { slug: "engineers", name: "Engineers",
    challenge: "Project-driven professionals who need organised records and timely compliance." },
  { slug: "consultants", name: "Consultants",
    challenge: "Independent consultants needing straightforward accounting and tax administration." },
  { slug: "contractors", name: "Independent Contractors",
    challenge: "Contractors who want their income, expenses and tax kept in order." },
  { slug: "entrepreneurs", name: "Entrepreneurs",
    challenge: "Founders who need financial visibility as they build and grow." },
  { slug: "small-business", name: "Small & Medium Businesses",
    challenge: "Owner-managed businesses needing practical, ongoing financial administration." },
  { slug: "professional-services", name: "Professional Services Businesses",
    challenge: "Service firms that need reliable monthly accounting and reporting." },
];

// ---- PROVINCES (SA) ----
export const provinces = [
  "Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo",
  "Mpumalanga", "North West", "Northern Cape", "Western Cape", "Outside South Africa",
];

// ---- CLIENT TYPES ----
export const clientTypes = [
  "Individual", "Sole Proprietor", "Company", "Professional Practice", "Partnership", "Other",
];

// ---- TURNOVER BANDS (configurable) ----
export const turnoverBands = [
  "Not yet trading", "Under R500,000", "R500,000 - R1 million", "R1 million - R1.5 million",
  "R1.5 million - R3 million", "R3 million - R5 million", "R5 million - R10 million",
  "Above R10 million", "Prefer not to say",
];

// ---- SERVICES REQUIRED (assessment multi-select) ----
export const serviceOptions = [
  "Bookkeeping", "Accounting", "Tax", "Annual Financial Statements", "Payroll", "VAT",
  "SARS Assistance", "Management Accounts", "Business/Financial Support", "Not Sure", "Other",
];

// ---- LEAD STATUSES (pipeline) ----
export const leadStatuses = [
  "NEW", "CONTACTED", "QUALIFIED", "CONSULTATION SCHEDULED",
  "CONSULTATION COMPLETED", "PROPOSAL SENT", "WON", "LOST", "NOT SUITABLE",
];

export const yearsOperatingOptions = [
  "Not yet trading", "Less than 1 year", "1 - 3 years", "3 - 5 years", "5 - 10 years", "10+ years",
];

export const urgencyOptions = ["Immediately", "Within 30 Days", "Within 3 Months", "Just Exploring"];
export const contactPreferences = ["Phone", "WhatsApp", "Email"];
export const recordsStatusOptions = ["Yes", "No", "Partially", "Not Sure"];

export default site;
